// context/SessionContext.js
import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { clearUserData, performSecurityCheck, updateActivity } from '../services/apiService';
import { getAuthItem, setAuthItem } from '../services/authSessionStore';
import { logSecurityEvent } from '../services/securityService';

const SessionContext = createContext();

/** Paths that must work without CTMS login (exact or prefix). */
function isPublicPath(pathname) {
  const p = String(pathname || '').toLowerCase();
  if (
    p === '/login' ||
    p === '/forgot-password' ||
    p === '/qmspreview' ||
    p === '/legal-policies' ||
    p === '/esource/fill'
  ) {
    return true;
  }
  // ePSF phone kit scanner — /s/{CODE} or /epsf-phone-scan…
  if (p === '/s' || p.startsWith('/s/')) return true;
  if (p === '/epsf-phone-scan' || p.startsWith('/epsf-phone-scan/')) return true;
  return false;
}

export const useSession = () => {
  const context = useContext(SessionContext);
  if (!context) {
    throw new Error('useSession must be used within a SessionProvider');
  }
  return context;
};

export const SessionProvider = ({ children }) => {
  // SESSION TIMEOUT CONFIGURATION - Change this value to modify timeout globally
  const INACTIVITY_TIMEOUT = 15; // Minutes - Change this to any value you want
  const WARNING_TIME = 2; // Minutes before logout to show warning
  
  const [showWarning, setShowWarning] = useState(false);
  const [timeLeft, setTimeLeft] = useState(0);
  const [isInitialized, setIsInitialized] = useState(false);
  
  const navigate = useNavigate();
  const location = useLocation();

  // Convert minutes to milliseconds
  const timeoutMs = INACTIVITY_TIMEOUT * 60 * 1000;
  const warningTimeMs = WARNING_TIME * 60 * 1000;

  // Activity events to track
  const activityEvents = [
    'mousedown',
    'mousemove', 
    'keypress',
    'scroll',
    'touchstart',
    'click',
    'focus'
  ];

  // Check if current route should be protected
  const isProtectedRoute = useCallback(() => {
    return !isPublicPath(location.pathname);
  }, [location.pathname]);

  // Reset activity timer
  const resetTimer = useCallback(() => {
    if (!isProtectedRoute()) return;
    
    setShowWarning(false);
    setTimeLeft(0);
    updateActivity();
    setAuthItem('sessionTimeout', INACTIVITY_TIMEOUT.toString());
  }, [isProtectedRoute, INACTIVITY_TIMEOUT]);

  // Logout function
  const logout = useCallback((reason = 'manual') => {
    // Clear all user data using API service
    clearUserData();
    
    setShowWarning(false);
    setTimeLeft(0);
    
    // Log security event
    logSecurityEvent('user_logout', { reason: reason });
    
    // Add reason to localStorage for displaying message on login page
    if (reason === 'timeout') {
      localStorage.setItem('logoutReason', 'session_expired');
    }
    
    navigate('/login', { replace: true });
  }, [navigate]);

  // Extend session
  const extendSession = useCallback(() => {
    logSecurityEvent('session_extended', { timeLeft: timeLeft });
    resetTimer();
  }, [resetTimer, timeLeft]);

  // Check session validity
  const checkSession = useCallback(() => {
    // Explicit check for public routes - skip all session checks
    if (isPublicPath(location.pathname)) {
      return;
    }
    
    if (!isProtectedRoute()) return;

    // Perform security check
    if (!performSecurityCheck()) {
      logout('security_check_failed');
      return;
    }

    const isLoggedIn = getAuthItem('isLoggedIn');
    const lastActivity = getAuthItem('lastActivity');
    const sessionTimeout = getAuthItem('sessionTimeout') || INACTIVITY_TIMEOUT;
    
    if (!isLoggedIn || isLoggedIn !== 'true') {
      // Don't redirect if already on a public page
      if (!isPublicPath(location.pathname)) {
        navigate('/login', { replace: true });
      }
      return;
    }

    if (lastActivity) {
      const timeDiff = Date.now() - parseInt(lastActivity);
      const timeoutInMs = parseInt(sessionTimeout) * 60 * 1000;
      
      if (timeDiff > timeoutInMs) {
        logout('timeout');
        return;
      }

      // Show warning if close to timeout
      if (timeDiff > (timeoutInMs - warningTimeMs)) {
        setShowWarning(true);
        setTimeLeft(Math.ceil((timeoutInMs - timeDiff) / 1000));
      } else {
        setShowWarning(false);
        setTimeLeft(0);
      }
    }
  }, [isProtectedRoute, INACTIVITY_TIMEOUT, warningTimeMs, logout, navigate, location.pathname]);

  // Initialize session on mount
  useEffect(() => {
    // Explicit check for public routes - skip session initialization
    if (isPublicPath(location.pathname)) {
      setIsInitialized(true);
      return;
    }
    
    if (isProtectedRoute()) {
      checkSession();
      
      // Set initial activity time if not exists
      if (!getAuthItem('lastActivity')) {
        resetTimer();
      }
    }
    setIsInitialized(true);
  }, [location.pathname, isProtectedRoute, checkSession, resetTimer]);

  // Set up activity listeners globally
  useEffect(() => {
    if (!isProtectedRoute()) return;

    const handleActivity = () => {
      resetTimer();
    };

    // Add event listeners to document
    activityEvents.forEach(event => {
      document.addEventListener(event, handleActivity, true);
    });

    return () => {
      // Cleanup event listeners
      activityEvents.forEach(event => {
        document.removeEventListener(event, handleActivity, true);
      });
    };
  }, [resetTimer, isProtectedRoute]);

  // Check session periodically
  useEffect(() => {
    if (!isProtectedRoute()) return;

    const interval = setInterval(checkSession, 30000); // Check every 30 seconds
    return () => clearInterval(interval);
  }, [checkSession, isProtectedRoute]);

  // Warning countdown timer
  useEffect(() => {
    let countdownInterval;
    
    if (showWarning && timeLeft > 0) {
      countdownInterval = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) {
            logout('timeout');
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => {
      if (countdownInterval) {
        clearInterval(countdownInterval);
      }
    };
  }, [showWarning, timeLeft, logout]);

  // Don't render children until session is initialized
  if (!isInitialized) {
    return (
      <div className="d-flex justify-content-center align-items-center vh-100">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  const value = {
    showWarning,
    timeLeft: Math.floor(timeLeft / 60), // Return minutes
    seconds: timeLeft % 60,
    extendSession,
    logout: () => logout('manual'),
    resetTimer,
    inactivityTimeout: INACTIVITY_TIMEOUT
  };

  return (
    <SessionContext.Provider value={value}>
      {children}
    </SessionContext.Provider>
  );
};
